import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CircleDollarSign,
  Clock,
  CircleCheckBig,
  SquarePen,
  Plus,
  Paperclip,
  X,
  Check,
  Trash2,
} from "lucide-react";
import AsyncSelect from "react-select/async";
import { toast } from "react-hot-toast";
import * as helperService from "../../../services/helperService";
import * as constantValues from "../../../constants/constants";
import { employeeService } from "../../../services/employeeService";
import {
  reimbursementService,
  extractErrorMessage,
} from "../../../services/reimbursementService";

const statusStyles = {
  Pending: "bg-yellow-100 text-yellow-700",
  Approved: "bg-blue-100 text-blue-700",
  Rejected: "bg-red-100 text-red-700",
  Paid: "bg-green-100 text-green-700",
};

const buildEmptyForm = () => ({
  name: "",
  amount: "",
  paid_by: null,
  payer: "",
  payment_type: "",
  date: "",
  status: "Pending",
  receipts: [],
});

const debounce = (fn, delay = 300) => {
  let timeout;
  return (...args) =>
    new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => resolve(await fn(...args)), delay);
    });
};

const ReceiptPreviewPanel = ({ preview, onClose }) => {
  if (!preview?.url) return null;

  return (
    <div className="mt-3 border border-canopy-border rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 border-b border-canopy-border flex items-center justify-between">
        <span className="text-sm font-semibold text-canopy-text truncate">
          Preview: {preview.label}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-canopy-textMuted hover:text-canopy-text rounded transition"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {preview.type.startsWith("image/") && (
        <img
          src={preview.url}
          alt="Receipt preview"
          className="max-h-96 w-auto object-contain mx-auto"
        />
      )}

      {preview.type === "application/pdf" && (
        <iframe title="PDF preview" src={preview.url} className="w-full h-96" />
      )}

      {(preview.type.startsWith("text/") ||
        preview.type === "application/json") && (
        <pre className="p-3 text-sm overflow-auto max-h-96 bg-gray-50">
          {preview.text ?? "—"}
        </pre>
      )}

      {!preview.type.startsWith("image/") &&
        preview.type !== "application/pdf" &&
        !preview.type.startsWith("text/") &&
        preview.type !== "application/json" && (
          <div className="p-3 text-sm text-canopy-textMuted">
            Preview not supported ({preview.type}).{" "}
            <button
              type="button"
              onClick={() => {
                const link = document.createElement("a");
                link.href = preview.url;
                link.download = preview.label || "receipt";
                link.click();
              }}
              className="text-canopy-blue hover:underline"
            >
              Download file
            </button>
          </div>
        )}
    </div>
  );
};

const ExpenseForm = ({
  title,
  submitLabel,
  formData,
  formError,
  receiptFiles,
  receiptInputRef,
  onReceiptInputChange,
  onOpenReceiptPicker,
  onRemoveReceiptFile,
  onPreviewSelectedReceipt,
  onPreviewExistingReceipt,
  onGetReceiptHref,
  activeReceiptPreview,
  previewLoadingKey,
  onClosePreview,
  isSubmitting,
  onChange,
  onEmployeeChange,
  onCancel,
  onSubmit,
  employeeDefaults,
  loadEmployeeOptions,
  canManageStatus,
}) => {
  const formPreview = activeReceiptPreview?.key?.startsWith("form-")
    ? activeReceiptPreview
    : null;

  return (
    <div className="border border-canopy-border rounded-xl p-5 bg-gray-50">
      <h3 className="text-2xl font-bold text-canopy-text mb-4">{title}</h3>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Service Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Amount
            </label>
            <input
              type="text"
              name="amount"
              value={formData.amount}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Paid By
            </label>
            <AsyncSelect
              isClearable
              cacheOptions
              defaultOptions={employeeDefaults}
              loadOptions={loadEmployeeOptions}
              value={
                formData.paid_by
                  ? { value: formData.paid_by, label: formData.payer }
                  : null
              }
              placeholder="Select a team member"
              onChange={onEmployeeChange}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Payment Type
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            >
              <option value="">Select type</option>
              <option value="Bank Account">Bank Account</option>
              <option value="Zelle">Zelle</option>
            </select>
          </div>
          {canManageStatus && (
            <div>
              <label className="block text-sm font-semibold text-canopy-text mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={onChange}
                className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
                required
              >
                <option value="">Select status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Receipts
            </label>
            <div className="space-y-3">
              <button
                type="button"
                onClick={onOpenReceiptPicker}
                className="inline-flex items-center justify-center px-4 py-2 border border-dashed border-canopy-border text-canopy-textMuted hover:text-canopy-blue hover:border-canopy-blue rounded-lg transition-colors"
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Upload Receipt
              </button>

              <input
                ref={receiptInputRef}
                type="file"
                multiple
                onChange={onReceiptInputChange}
                className="hidden"
              />

              {formData.receipts?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
                    Existing Receipts
                  </p>
                  {formData.receipts.map((receipt, index) => {
                    const label =
                      receipt.file_name ||
                      receipt.stored_name ||
                      `Receipt ${index + 1}`;
                    const previewKey = `form-existing-${index}`;
                    const receiptHref = receipt.path
                      ? onGetReceiptHref(receipt.path)
                      : "";

                    return (
                      <div
                        key={`${label}-${index}`}
                        className="flex flex-wrap items-center gap-2 text-sm text-canopy-textMuted"
                      >
                        <Paperclip className="h-4 w-4" />
                        <span className="break-all">{label}</span>
                        {receipt.path && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                onPreviewExistingReceipt(
                                  receipt,
                                  previewKey,
                                  label,
                                )
                              }
                              className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                            >
                              {previewLoadingKey === previewKey
                                ? "Loading..."
                                : "Preview"}
                            </button>
                            <a
                              href={receiptHref}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-canopy-blue hover:underline"
                            >
                              Open
                            </a>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {receiptFiles?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
                    New Uploads
                  </p>
                  {receiptFiles.map((file, index) => {
                    const previewKey = `form-new-${index}`;
                    return (
                      <div
                        key={`${file.name}-${file.lastModified}-${index}`}
                        className="flex flex-wrap items-center gap-2 text-sm text-canopy-textMuted"
                      >
                        <Paperclip className="h-4 w-4" />
                        <span className="break-all">{file.name}</span>
                        <span className="text-xs text-canopy-textMuted">
                          • {helperService.formatBytes(file.size)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            onPreviewSelectedReceipt(
                              file,
                              previewKey,
                              file.name,
                            )
                          }
                          className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                        >
                          {previewLoadingKey === previewKey
                            ? "Loading..."
                            : "Preview"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveReceiptFile(index)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <ReceiptPreviewPanel
              preview={formPreview}
              onClose={onClosePreview}
            />
          </div>
        </div>

        {formError && (
          <p className="text-sm text-red-600 font-semibold">{formError}</p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-canopy-border hover:bg-white transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="canopy-btn-primary"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReimbursementsOverview = ({ userPermission, currentEmployee }) => {
  const [expenses, setExpenses] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [formData, setFormData] = useState(buildEmptyForm());
  const [receiptFiles, setReceiptFiles] = useState([]);
  const receiptInputRef = useRef(null);
  const [activeReceiptPreview, setActiveReceiptPreview] = useState(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [employeeDefaults, setEmployeeDefaults] = useState([]);
  const [activeFilter, setActiveFilter] = useState({ key: null, value: null });
  const [dataLoaded, setDataLoaded] = useState(false);

  const canManageStatus =
    currentEmployee?.is_hr_manager === "Yes" ||
    currentEmployee?.level === "Manager" ||
    currentEmployee?.level === "Partner";
  const isAdmin = userPermission === "masterAccess";

  // Capture action params from email deep-link on first render, then clear the URL
  const pendingActionRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const reimbId = params.get("reimb_id");
    if (action && reimbId) {
      pendingActionRef.current = { action, reimbId };
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ items }, employees] = await Promise.all([
          reimbursementService.getReimbursements(),
          employeeService.getManagers("", []),
        ]);
        setExpenses(items);
        setEmployeeDefaults(
          employees.map((r) => ({ value: r.id, label: r.name })),
        );
      } catch (err) {
        console.error("Failed to load reimbursements", err);
      } finally {
        setDataLoaded(true);
      }
    })();
  }, []);

  // After expenses load, execute any pending action from an email deep-link
  useEffect(() => {
    if (!dataLoaded || !pendingActionRef.current || !canManageStatus) return;
    const { action, reimbId } = pendingActionRef.current;
    pendingActionRef.current = null;
    const expense = expenses.find((e) => String(e.id) === String(reimbId));
    if (!expense || expense.status !== "Pending") return;
    if (action === "approve") {
      handleApproveExpense(expense);
    } else if (action === "reject") {
      handleRejectExpense(expense);
    }
  }, [dataLoaded, expenses, canManageStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (activeReceiptPreview?.url) {
        URL.revokeObjectURL(activeReceiptPreview.url);
      }
    };
  }, [activeReceiptPreview]);

  const loadEmployeeOptions = async (inputValue) => {
    const rows = await employeeService.getManagers(inputValue, []);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const debouncedLoadEmployees = useMemo(
    () => debounce(loadEmployeeOptions),
    [],
  );

  const handleEmployeeChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      paid_by: option ? option.value : null,
      payer: option ? option.label : "",
    }));
  };

  const years = useMemo(() => {
    const set = new Set(
      expenses.map((r) => r.date?.slice(0, 4)).filter(Boolean),
    );
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [expenses]);

  const filteredRegistries = useMemo(() => {
    return expenses.filter((r) => {
      const y = r.date?.slice(0, 4);
      const m = r.date?.slice(5, 7);

      const matchYear = selectedYear === "all" || y === selectedYear;
      const matchMonth = selectedMonth === "all" || m === selectedMonth;

      return matchYear && matchMonth;
    });
  }, [expenses, selectedYear, selectedMonth]);

  const stats = useMemo(() => {
    const expenseTotal = filteredRegistries.reduce(
      (sum, e) => sum + Number(e.amount),
      0,
    );
    const pendingExpense = filteredRegistries
      .filter((e) => e.status === "Pending")
      .reduce((sum, e) => sum + Number(e.amount), 0);
    const paidExpense = filteredRegistries
      .filter((e) => e.status === "Paid")
      .reduce((sum, e) => sum + Number(e.amount), 0);

    return [
      {
        label: "Total",
        value: expenseTotal,
        helper: "All expenses",
        icon: CircleDollarSign,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: null,
        filterValue: null,
      },
      {
        label: "Pending",
        value: pendingExpense,
        helper: "Pending payment",
        icon: Clock,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterKey: "status",
        filterValue: "Pending",
      },
      {
        label: "Paid",
        value: paidExpense,
        helper: "Paid expenses",
        icon: CircleCheckBig,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterKey: "status",
        filterValue: "Paid",
      },
    ];
  }, [filteredRegistries]);

  const filteredExpenses = useMemo(() => {
    const list = activeFilter.key
      ? filteredRegistries.filter(
          (e) => e[activeFilter.key] === activeFilter.value,
        )
      : filteredRegistries;
    return [...list].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (bTime !== aTime) return bTime - aTime;
      return Number(b.id) - Number(a.id);
    });
  }, [filteredRegistries, activeFilter]);

  const handleStatClick = (filterKey, filterValue) => {
    if (
      !filterKey ||
      (activeFilter.key === filterKey && activeFilter.value === filterValue)
    ) {
      setActiveFilter({ key: null, value: null });
    } else {
      setActiveFilter({ key: filterKey, value: filterValue });
    }
  };

  const closeReceiptPreview = () => {
    setActiveReceiptPreview((previous) => {
      if (previous?.url) URL.revokeObjectURL(previous.url);
      return null;
    });
    setPreviewLoadingKey(null);
  };

  const setPreviewFromBlob = async ({ key, label, blob, contentType }) => {
    const normalizedType =
      contentType || blob.type || "application/octet-stream";
    let text = null;
    if (
      normalizedType.startsWith("text/") ||
      normalizedType === "application/json"
    ) {
      try {
        text = await blob.text();
      } catch {
        text = null;
      }
    }
    const url = URL.createObjectURL(blob);

    setActiveReceiptPreview((previous) => {
      if (previous?.url) URL.revokeObjectURL(previous.url);
      return { key, label, url, type: normalizedType, text };
    });
  };

  const handlePreviewSelectedReceipt = async (file, key, label) => {
    setPreviewLoadingKey(key);
    setFormError("");
    try {
      await setPreviewFromBlob({
        key,
        label,
        blob: file,
        contentType: file.type || "application/octet-stream",
      });
    } catch (error) {
      setFormError("Unable to preview selected file.");
      console.error(error);
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const handlePreviewSavedReceipt = async (receipt, key, label) => {
    if (!receipt?.path) return;
    setPreviewLoadingKey(key);
    setFormError("");
    try {
      const { blob, contentType } = await reimbursementService.getReceiptFile(
        receipt.path,
      );
      await setPreviewFromBlob({ key, label, blob, contentType });
    } catch (error) {
      setFormError("Unable to load receipt preview.");
      console.error(error);
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const handleOpenReceiptPicker = () => {
    receiptInputRef.current?.click();
  };

  const handleReceiptInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setReceiptFiles((previous) => [...previous, ...files]);
    event.target.value = "";
  };

  const handleRemoveReceiptFile = (index) => {
    setReceiptFiles((previous) => previous.filter((_, idx) => idx !== index));
    closeReceiptPreview();
  };

  const closeForm = () => {
    closeReceiptPreview();
    setFormMode(null);
    setEditingExpenseId(null);
    setFormData(buildEmptyForm());
    setReceiptFiles([]);
    setIsSubmitting(false);
    setFormError("");
    if (receiptInputRef.current) receiptInputRef.current.value = "";
  };

  const openCreateForm = () => {
    closeReceiptPreview();
    setFormMode("create");
    setEditingExpenseId(null);
    setFormData(buildEmptyForm());
    setReceiptFiles([]);
    setFormError("");
    if (receiptInputRef.current) receiptInputRef.current.value = "";
  };

  const openEditForm = (expense) => {
    closeReceiptPreview();
    setFormMode("edit");
    setEditingExpenseId(expense.id);
    setFormData({
      name: expense.name,
      amount: expense.amount,
      paid_by: expense.paid_by ?? null,
      payer: expense.payer,
      payment_type: expense.payment_type,
      date: expense.date,
      status: expense.status,
      receipts: expense.receipts || [],
    });
    setReceiptFiles([]);
    setFormError("");
    if (receiptInputRef.current) receiptInputRef.current.value = "";
  };

  const handleApproveExpense = async (expense) => {
    try {
      const updated = await reimbursementService.updateReimbursement(
        expense.id,
        {
          ...expense,
          status: "Approved",
          approved_by: currentEmployee?.id ?? null,
        },
      );
      setExpenses((previous) =>
        previous.map((e) => (e.id === expense.id ? updated : e)),
      );
      toast.success("Expense approved");
    } catch (error) {
      console.error("Failed to approve expense", error);
      toast.error("Failed to approve expense");
    }
  };

  const handleRejectExpense = async (expense) => {
    try {
      const updated = await reimbursementService.updateReimbursement(
        expense.id,
        {
          ...expense,
          status: "Rejected",
          approved_by: currentEmployee?.id ?? null,
        },
      );
      setExpenses((previous) =>
        previous.map((e) => (e.id === expense.id ? updated : e)),
      );
      toast.success("Expense rejected");
    } catch (error) {
      console.error("Failed to reject expense", error);
      toast.error("Failed to reject expense");
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!isAdmin) {
      toast.error("You do not have permission to delete this expense");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete "${expense.name}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await reimbursementService.deleteReimbursement(expense.id);
      setExpenses((previous) => previous.filter((e) => e.id !== expense.id));
      toast.success("Expense deleted");
    } catch (error) {
      console.error("Failed to delete expense", error);
      toast.error("Failed to delete expense");
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "amount" ? value.replace(/[^\d.]/g, "") : value;
    setFormData((previous) => ({
      ...previous,
      [name]: nextValue,
    }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      let uploadedReceipts = formData.receipts || [];
      if (receiptFiles.length > 0) {
        const uploads = await Promise.all(
          receiptFiles.map((file) =>
            reimbursementService.uploadReceiptFile(file),
          ),
        );
        const uploadedItems = uploads.map((item, index) => ({
          file_name: item.file_name || receiptFiles[index]?.name || "Receipt",
          stored_name: item.stored_name || "",
          path: item.path || "",
        }));
        uploadedReceipts = [...uploadedReceipts, ...uploadedItems];
      }

      const payload = {
        ...formData,
        receipts: uploadedReceipts,
      };

      if (formMode === "edit" && editingExpenseId) {
        const updated = await reimbursementService.updateReimbursement(
          editingExpenseId,
          payload,
        );
        setExpenses((previous) =>
          previous.map((expense) =>
            expense.id === editingExpenseId ? updated : expense,
          ),
        );
      } else {
        const created = await reimbursementService.createReimbursement(payload);
        setExpenses((previous) => [...previous, created]);
      }
      closeForm();
    } catch (error) {
      setFormError(
        extractErrorMessage(
          error,
          error?.message || "Unable to upload receipt. Try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.filterKey === null
                ? activeFilter.key === null
                : activeFilter.key === item.filterKey &&
                  activeFilter.value === item.filterValue;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  handleStatClick(item.filterKey, item.filterValue)
                }
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  ${item.value.toFixed(2)}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
            Expenses History
          </h2>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <select
              className="w-full md:w-auto border border-canopy-border rounded-lg px-3 py-2 text-sm font-semibold bg-white"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">All years</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <select
              className="w-full md:w-auto border border-canopy-border rounded-lg px-3 py-2 text-sm font-semibold bg-white"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="all">All months</option>
              {constantValues.MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            {/* {isAdminView && ( */}
            <button
              type="button"
              onClick={openCreateForm}
              className="canopy-btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
            >
              <Plus className="w-6 h-6" />
            </button>
            {/* )} */}
          </div>
        </div>

        {formMode === "create" && (
          <div className="mt-6">
            <ExpenseForm
              title="Create an Expense"
              submitLabel="Create"
              formData={formData}
              formError={formError}
              receiptFiles={receiptFiles}
              receiptInputRef={receiptInputRef}
              onReceiptInputChange={handleReceiptInputChange}
              onOpenReceiptPicker={handleOpenReceiptPicker}
              onRemoveReceiptFile={handleRemoveReceiptFile}
              onPreviewSelectedReceipt={handlePreviewSelectedReceipt}
              onPreviewExistingReceipt={handlePreviewSavedReceipt}
              onGetReceiptHref={reimbursementService.getReceiptFileUrl}
              activeReceiptPreview={activeReceiptPreview}
              previewLoadingKey={previewLoadingKey}
              onClosePreview={closeReceiptPreview}
              isSubmitting={isSubmitting}
              onChange={handleFormChange}
              onEmployeeChange={handleEmployeeChange}
              onCancel={closeForm}
              onSubmit={handleFormSubmit}
              employeeDefaults={employeeDefaults}
              loadEmployeeOptions={debouncedLoadEmployees}
              canManageStatus={canManageStatus}
            />
          </div>
        )}

        <div className="mt-6 space-y-4">
          {filteredExpenses.map((expense) => {
            const isEditingThisExpense =
              formMode === "edit" && editingExpenseId === expense.id;

            if (isEditingThisExpense) {
              return (
                <div
                  key={expense.id}
                  className="border border-canopy-border rounded-xl p-5 bg-white"
                >
                  <ExpenseForm
                    title="Edit Expense"
                    submitLabel="Save Changes"
                    formData={formData}
                    formError={formError}
                    receiptFiles={receiptFiles}
                    receiptInputRef={receiptInputRef}
                    onReceiptInputChange={handleReceiptInputChange}
                    onOpenReceiptPicker={handleOpenReceiptPicker}
                    onRemoveReceiptFile={handleRemoveReceiptFile}
                    onPreviewSelectedReceipt={handlePreviewSelectedReceipt}
                    onPreviewExistingReceipt={handlePreviewSavedReceipt}
                    onGetReceiptHref={reimbursementService.getReceiptFileUrl}
                    activeReceiptPreview={activeReceiptPreview}
                    previewLoadingKey={previewLoadingKey}
                    onClosePreview={closeReceiptPreview}
                    isSubmitting={isSubmitting}
                    onChange={handleFormChange}
                    onEmployeeChange={handleEmployeeChange}
                    onCancel={closeForm}
                    onSubmit={handleFormSubmit}
                    employeeDefaults={employeeDefaults}
                    loadEmployeeOptions={debouncedLoadEmployees}
                    canManageStatus={canManageStatus}
                  />
                </div>
              );
            }

            return (
              <div
                key={expense.id}
                className="border border-canopy-border rounded-xl p-5 bg-white"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-indigo-500 text-white flex items-center justify-center font-semibold">
                      <CircleDollarSign className="h-4 w-4" />
                    </div>

                    <div>
                      <p className="text-2xl font-bold text-canopy-text">
                        {expense.name}
                      </p>
                      <p className="text-sm text-canopy-textMuted">
                        Amount: ${expense.amount}
                      </p>
                      <p className="text-sm text-canopy-textMuted">
                        {expense.payment_type} •{" "}
                        {helperService.formatJoinedDate(expense.date)}
                      </p>
                      {expense.payer && (
                        <p className="text-xs text-canopy-textMuted mt-1">
                          Paid by{" "}
                          <span className="font-semibold">{expense.payer}</span>
                        </p>
                      )}
                      {expense.approver_name && (
                        <p className="text-xs text-canopy-textMuted mt-1">
                          {expense.status === "Rejected"
                            ? "Rejected"
                            : "Approved"}{" "}
                          by{" "}
                          <span className="font-semibold">
                            {expense.approver_name}
                          </span>
                        </p>
                      )}
                      {expense.receipts?.length > 0 && (
                        <div className="text-sm text-canopy-textMuted mt-1">
                          Receipts:
                          <div className="mt-1 flex flex-col gap-1">
                            {expense.receipts.map((receipt, index) => {
                              const label =
                                receipt.file_name ||
                                receipt.stored_name ||
                                `Receipt ${index + 1}`;
                              const previewKey = `card-${expense.id}-${index}`;
                              const url = receipt.path
                                ? reimbursementService.getReceiptFileUrl(
                                    receipt.path,
                                  )
                                : "";

                              if (!url) {
                                return (
                                  <span key={`${label}-${index}`}>{label}</span>
                                );
                              }

                              return (
                                <div
                                  key={`${label}-${index}`}
                                  className="flex flex-wrap items-center gap-2"
                                >
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-canopy-blue hover:underline break-all"
                                  >
                                    {label}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePreviewSavedReceipt(
                                        receipt,
                                        previewKey,
                                        label,
                                      )
                                    }
                                    className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                                  >
                                    {previewLoadingKey === previewKey
                                      ? "Loading..."
                                      : "Preview"}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {activeReceiptPreview?.key?.startsWith(
                            `card-${expense.id}-`,
                          ) && (
                            <ReceiptPreviewPanel
                              preview={activeReceiptPreview}
                              onClose={closeReceiptPreview}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        statusStyles[expense.status] ??
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {expense.status}
                    </span>

                    {canManageStatus && expense.status === "Pending" && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApproveExpense(expense)}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-green-100 transition"
                          title="Approve"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRejectExpense(expense)}
                          className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-red-100 transition"
                          title="Reject"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => openEditForm(expense)}
                      className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-gray-100 transition"
                    >
                      <SquarePen className="h-3 w-3" />
                    </button>

                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteExpense(expense)}
                        className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-red-100 transition"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ReimbursementsOverview;
